import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, InputNumber, DatePicker, Button, Space, Table, Tag, message, Select, Checkbox } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const InstructionReconcilePage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [exportFields, setExportFields] = useState<string[]>(['id','system_code','currency','amount','occurred_on','created_at','source']);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const initial: any = {};
      const iid = sp.get('instruction_id');
      if (iid) initial.instruction_id = Number(iid);
      if (Object.keys(initial).length) form.setFieldsValue(initial);
    } catch {}
  }, []);

  const fetchData = async () => {
    try {
      const v = await form.validateFields();
      if (!v.instruction_id) { message.warning('请输入指令ID'); return; }
      setLoading(true);
      const resp = await fetch(`/api/v1/billing/billing-records/by-instruction/${v.instruction_id}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('加载失败');
      const data = await resp.json();
      const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      setRows(list);
      // 初始过滤
      applyFilter(list);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    try {
      const v = await form.validateFields();
      if (!v.instruction_id) { message.warning('请输入指令ID'); return; }
      // 客制字段导出：前端根据过滤后的数据生成
      const cols = exportFields.length ? exportFields : ['id','system_code','currency','amount','occurred_on','created_at','source'];
      const header = cols.join(',') + '\n';
      const lines = (filtered.length ? filtered : rows).map(r => cols.map(k => JSON.stringify(r[k] ?? '')).join(',')).join('\n');
      const text = header + lines;
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `instruction-${v.instruction_id}-reconcile.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '导出失败');
    }
  };

  const totalsByCurrency = useMemo(() => {
    const map: Record<string, number> = {};
    (filtered.length ? filtered : rows).forEach(r => {
      const cur = r.currency || 'CNY';
      const amt = parseFloat(r.amount || '0') || 0;
      map[cur] = (map[cur] || 0) + amt;
    });
    return map;
  }, [rows, filtered]);

  const applyFilter = (source?: any[]) => {
    const v = form.getFieldsValue();
    let arr = (source || rows).slice();
    // 币种
    if (v.currency) {
      arr = arr.filter((x:any) => String(x.currency || 'CNY') === String(v.currency));
    }
    // 发生日期范围
    if (v.range && Array.isArray(v.range) && v.range[0] && v.range[1]) {
      const [start, end] = v.range;
      arr = arr.filter((x:any) => {
        const d = x.occurred_on ? dayjs(x.occurred_on) : null;
        return d ? (d.isSame(start, 'day') || d.isAfter(start, 'day')) && (d.isSame(end, 'day') || d.isBefore(end, 'day')) : false;
      });
    }
    setFiltered(arr);
    setPage(1);
  };

  return (
    <div className="list-page-container">
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>指令级对账</div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData}>刷新</Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={exportCSV}>导出CSV</Button>
          </Space>
        </div>
        <Form form={form} layout="inline" onFinish={fetchData} onValuesChange={() => applyFilter()}>
          <Form.Item name="instruction_id" label="指令ID" rules={[{ required: true, message: '必填' }]}>
            <InputNumber style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="currency" label="币种">
            <Select allowClear style={{ width: 120 }}>
              <Select.Option value="CNY">CNY</Select.Option>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="range" label="发生日期">
            <DatePicker.RangePicker allowClear />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">查询</Button>
              <Button onClick={() => { form.resetFields(); setRows([]); }}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
        <div style={{ margin: '8px 0' }}>
          {Object.keys(totalsByCurrency).length === 0 ? (
            <span style={{ color: '#999' }}>暂无记录</span>
          ) : (
            <Space>
              {Object.entries(totalsByCurrency).map(([cur, amt]) => (
                <Tag key={cur} color="blue">{cur} {amt}</Tag>
              ))}
            </Space>
          )}
        </div>
        <div style={{ margin: '8px 0' }}>
          <Space>
            <span>导出字段：</span>
            <Checkbox.Group
              options={[
                { label: 'ID', value: 'id' },
                { label: '系统编码', value: 'system_code' },
                { label: '币种', value: 'currency' },
                { label: '金额', value: 'amount' },
                { label: '发生日期', value: 'occurred_on' },
                { label: '创建时间', value: 'created_at' },
                { label: '来源', value: 'source' },
              ]}
              value={exportFields}
              onChange={(vals:any) => setExportFields(vals as string[])}
            />
          </Space>
        </div>
        <Table
          size="small"
          rowKey={(r:any, i?: number) => String(r?.id ?? i)}
          loading={loading}
          dataSource={(filtered.length ? filtered : rows).slice((page-1)*pageSize, (page-1)*pageSize + pageSize)}
          pagination={{
            current: page,
            pageSize,
            total: (filtered.length ? filtered : rows).length,
            showSizeChanger: true,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 100 },
            { title: '系统编码', dataIndex: 'system_code', width: 180, render: (v:any) => v || '-' },
            { title: '币种', dataIndex: 'currency', width: 80 },
            { title: '金额', dataIndex: 'amount', width: 120 },
            { title: '发生日期', dataIndex: 'occurred_on', width: 140, render: (v:any) => v || '-' },
            { title: '创建时间', dataIndex: 'created_at', width: 180, render: (v:any) => v || '-' },
            { title: '来源', dataIndex: 'source', width: 90 },
          ]}
        />
      </Card>
    </div>
  );
};

export default InstructionReconcilePage;


